<template lang="html">
  <div class="custom-image-upload">
    <div class="upload-wrapper">
      <div class="upload-button-wrapper">
        <div class="relative-draggable-image-wrapper">
          <input
            name="file"
            type="file"
            ref="input"
            :multiple="multiple"
            :disabled="disableUpload"
            @input="
              event => handleChange(event, multiple ? 'Multiple' : 'Single')
            "
            class="upload-select"
            accept="image/jpg, image/png, image/jpeg"
          />
          <p class="upload-drag-icon">
            <a-icon type="inbox" />
          </p>
          <p v-if="!disableUpload" class="upload-text">
            {{ label }}
          </p>
          <p v-if="multiple && !disableUpload" class="upload-text">
            (Maximum {{ uploadLimit }} images are allowed)
          </p>
          <p v-if="disableUpload" class="upload-text">
            Only {{ uploadLimit }} Pictures allowed
          </p>
        </div>
      </div>
      <span
        v-if="(imageSrc !== '' || !_.isEmpty(imageArray)) && multiple"
        class="multi-image-title"
      >
        Preview
      </span>
      <div
        class="image-wrapper"
        v-if="imageSrc !== '' || !_.isEmpty(imageArray)"
      >
        <div v-if="multiple">
          <div class="d-flex-spc-btwn">
            <div
              class="multiple-image-wrapper"
              v-for="(item, index) in imageArray"
              :key="index"
            >
              <div class="img-wrap">
                <div class="preview-delete">
                  <div class="preview">
                    <a-icon type="eye" @click="showModal(index)" />
                  </div>
                  <div class="delete">
                    <a-icon type="delete" @click="handleImageDelete(index)" />
                  </div>
                </div>
                <img class="single-image-wrapper" :src="item" v-if="item" />
              </div>
            </div>
          </div>
          <a-modal
            class="custom-modal video-modal"
            v-model="visible"
            :footer="null"
          >
            <div class="modal-img-wrap">
              <img :src="imageArray[imageIndex]" />
            </div>
          </a-modal>
        </div>
        <div v-else>
          <div class="img-wrap">
            <div class="preview-delete">
              <div :class="cannotDelete ? 'preview-center' : 'preview'">
                <a-icon type="eye" @click="showModal" />
              </div>
              <div v-if="!cannotDelete" class="delete" @click="removeSingle">
                <a-icon type="delete" />
              </div>
            </div>
            <img class="single-image-wrapper" :src="imageSrc" v-if="imageSrc" />
          </div>
          <a-modal
            class="custom-modal video-modal"
            v-model="visible"
            :footer="null"
          >
            <div class="modal-img-wrap">
              <img :src="imageSrc" />
            </div>
          </a-modal>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import isEmpty from "@/utils/isEmpty";

export default {
  data() {
    return {
      imageSrc: "",
      visible: false,
      imageArray: [],
      multipleImageData: [],
      imageIndex: 0
    };
  },
  props: {
    multiple: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: "Click this area to upload"
    },
    imageUrl: {
      type: String,
      default: ""
    },
    uploadLimit: {
      type: Number,
      default: 1
    },
    uploadSizeLimitInMb: {
      type: Number,
      default: 4
    },
    previewImages: {
      type: Array,
      default: () => []
    },
    cannotDelete: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    disableUpload() {
      return (
        this.multipleImageData.length === this.uploadLimit ||
        this.imageArray.length >= this.uploadLimit ||
        this.previewImages.length >= this.uploadLimit
      );
    },
    imageSrcEmpty() {
      return Boolean(this.imageSrc);
    }
  },
  mounted() {
    if (!isEmpty(this.imageUrl)) {
      this.imageSrc = this.generateImageUrl(this.imageUrl);
    }
  },
  methods: {
    showModal(index) {
      this.imageIndex = index;
      this.visible = true;
    },
    removeSingle() {
      this.imageSrc = "";
      this.$emit("file-upload", null);
    },
    handleImageDelete(index) {
      if (
        this.previewImages.includes(this.imageArray[index].split("/").pop())
      ) {
        this.$emit("file-delete", this.previewImages[index]);
        this.previewImages.splice(index, 1);
      }
      this.imageArray.splice(index, 1);
      this.multipleImageData.splice(index, 1);
      this.$emit("file-upload", this.multipleImageData);
    },
    handleFileUpload() {
      this.$refs["input"].click();
    },
    handleChange(event, type = "Single") {
      this[`handle${type}Upload`](event);
    },
    handleMultipleUpload(event) {
      let loopLimit =
        event.target.files.length > this.uploadLimit
          ? this.uploadLimit
          : event.target.files.length;
      for (let i = 0; i < loopLimit; i++) {
        let fileSize = event.target.files[i].size / (1024 * 1024);
        let fileType = event.target.files[i].type.split("/")[0];
        if (fileSize <= this.uploadSizeLimitInMb && fileType === "image") {
          this.multipleImageData.push(event.target.files[i]);
          const reader = new FileReader();
          reader.onload = e => {
            if (this.imageArray.length >= this.uploadLimit) {
              this.imageArray.pop();
            }
            this.imageArray.push(e.target.result);
          };
          reader.readAsDataURL(event.target.files[i]);
        } else {
          this.$notification.error({
            message: "Error",
            description:
              fileSize > this.uploadSizeLimitInMb
                ? `File Size Limit exceeded by ${event.target.files[i].name}. ${this.uploadSizeLimitInMb}MB limit`
                : `${event.target.files[i].name}'s format is not supported`
          });
        }
      }
      this.$emit("file-upload", this.multipleImageData);
    },
    handleSingleUpload(event) {
      const reader = new FileReader();
      let fileSize = event.target.files[0].size / (1024 * 1024);
      let fileType = event.target.files[0].type.split("/")[0];
      if (fileSize <= this.uploadSizeLimitInMb && fileType === "image") {
        reader.onload = e => {
          this.imageSrc = e.target.result;
        };
        reader.readAsDataURL(event.target.files[0]);
      } else {
        this.$notification.error({
          message: "Error",
          description:
            fileSize > this.uploadSizeLimitInMb
              ? `File Size Limit exceeded by ${event.target.files[0].name}. ${this.uploadSizeLimitInMb}MB limit`
              : "Unsupported File Format"
        });
      }
      this.$emit("file-upload", event.target.files[0]);
    },
    handleClearAll() {
      this.multipleImageData = [];
      this.imageArray = [];
      this.$emit("file-upload", this.multipleImageData);
    }
  },
  watch: {
    imageUrl(newVal) {
      this.imageSrc = newVal ? this.generateImageUrl(newVal) : "";
    },
    previewImages(newVal) {
      this.imageArray = newVal
        ? newVal.map(item => this.generateImageUrl(item))
        : "";
    }
  }
};
</script>

<style lang="scss">
.custom-image-upload {
  margin-bottom: 20px;
  .upload-wrapper {
    height: 100%;
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: center;
    padding: 30px 25px;
    .upload-button-wrapper {
      position: relative;
      width: 60%;
      height: 100%;
      padding: 30px 0 16px;
      text-align: center;
      background: #fafafa;
      border: 1px dashed #d9d9d9;
      border-radius: 4px;
      cursor: pointer;
      -webkit-transition: border-color 0.3s;
      transition: border-color 0.3s;
      .upload-drag-icon {
        .anticon {
          color: #3c4498;
          font-size: 48px;
        }
      }
      .upload-text {
        color: rgba(0, 0, 0, 0.85);
        font-size: 16px;
      }
      &:hover {
        border-color: #40a9ff;
      }
    }
    .img-wrap {
      width: 110px;
      height: 110px;
      padding: 5px;
      margin-right: 15px;
      border-radius: 4px;
      border: 1px dashed #29d629;
      position: relative;
      .preview-delete {
        position: absolute;
        left: 0;
        top: 0;
        opacity: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.4);
        transition: all 0.2s ease-in-out;
        display: flex;
        justify-content: center;
        align-items: center;
        .anticon {
          color: #fff;
          cursor: pointer;
          font-size: 16px;
        }
        .preview {
          margin-right: 20px;
        }
        .preview-center {
          margin-right: 0px;
        }
      }
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      &:hover {
        .preview-delete {
          opacity: 1;
        }
      }
    }
    .multi-image-title {
      font-size: 18px;
      font-weight: 400;
      color: #fff;
      padding: 30px 0 20px;
      text-align: center;
    }
  }
  .relative-draggable-image-wrapper {
    position: relative;

    input {
      position: absolute;
      opacity: 0;
      width: fill-available;
      height: 10rem !important;
      top: -25px;
    }
  }
}
@media only screen and (max-width: 575px) {
  .custom-image-upload {
    .upload-wrapper {
      height: 100%;
      padding: 20px 10px;
      flex-direction: column-reverse;
      .img-wrap {
        margin: 0 0 15px 0;
      }
      .upload-button-wrapper {
        width: 100% !important;
      }
      .image-wrapper {
        .d-flex-spc-btwn {
          flex-direction: column;
        }
      }
    }
  }
}
</style>
